import { motion } from "framer-motion";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { useTranslation } from "react-i18next";
import { Sparkles, TrendingUp, AlertTriangle } from "lucide-react";



export const HeroVisual = () => {
    const { t } = useTranslation("common");

    const data = [
        { name: t("time.mon"), value: 4000 },
        { name: t("time.tue"), value: 3000 },
        { name: t("time.wed"), value: 5000 },
        { name: t("time.thu"), value: 2780 },
        { name: t("time.fri"), value: 1890 },
        { name: t("time.sat"), value: 2390 },
        { name: t("time.sun"), value: 3490 },
    ];

    return (
        <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative w-full max-w-5xl mx-auto"
        >
            {/* Main Dashboard Window */}
            <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl overflow-hidden border border-neutral-200/60 dark:border-neutral-700/60 backdrop-blur-xl">
                {/* Fake Window Header */}
                <div className="h-10 border-b border-neutral-200 dark:border-neutral-700 flex items-center px-4 space-x-2 bg-neutral-50/50 dark:bg-neutral-800/50">
                    <div className="flex space-x-1.5">
                        <div className="w-3 h-3 rounded-full bg-error-500/80" />
                        <div className="w-3 h-3 rounded-full bg-warning-500/80" />
                        <div className="w-3 h-3 rounded-full bg-success-500/80" />
                    </div>
                    <div className="flex-1 text-center">
                        <div className="inline-block px-3 py-1 bg-white dark:bg-neutral-900 rounded-md shadow-sm text-xs font-medium text-neutral-500 dark:text-neutral-400 border border-neutral-100 dark:border-neutral-800">
                            {t("brandName")}
                        </div>
                    </div>
                </div>

                {/* Dashboard Content */}
                <div className="p-4 md:p-8 bg-background-page">
                    {/* Top Stats Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                        {[
                            { label: t("landing.hero.totalRevenue"), value: "$42,593.00", change: "+12.5%", color: "text-success-600" },
                            { label: t("landing.hero.netProfit"), value: "$8,230.50", change: "+8.2%", color: "text-success-600" },
                            { label: t("landing.hero.adSpend"), value: "$12,450.00", change: "-2.1%", color: "text-success-600" },
                        ].map((stat, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 + i * 0.1 }}
                                className="p-4 bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700"
                            >
                                <div className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mb-1">{stat.label}</div>
                                <div className="text-lg md:text-2xl font-bold text-text-primary">{stat.value}</div>
                                <div className={`text-[10px] sm:text-xs font-medium ${stat.color} mt-1`}>{stat.change} {t("landing.hero.vsLastWeek")}</div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Main Chart Area */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 p-4 sm:p-6 bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-neutral-200 dark:border-neutral-700">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                                <h3 className="font-semibold text-text-primary text-sm sm:text-base">{t("landing.hero.revenueGrowth")}</h3>
                                <div className="flex gap-2">
                                    <div className="px-3 py-1 text-[10px] sm:text-xs font-medium bg-neutral-100 dark:bg-neutral-700 rounded-full text-neutral-600 dark:text-neutral-300">{t("landing.hero.thisMonth")}</div>
                                </div>
                            </div>
                            <div className="h-48 sm:h-64 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={data}>
                                        <defs>
                                            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#c7613d" stopOpacity={0.1} />
                                                <stop offset="95%" stopColor="#c7613d" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9ca3af' }} />
                                        <Tooltip
                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                            cursor={{ stroke: '#c7613d', strokeWidth: 1, strokeDasharray: '4 4' }}
                                        />
                                        <Area type="monotone" dataKey="value" stroke="#c7613d" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Insight Panel */}
                        <div className="lg:col-span-1 space-y-4">
                            <div className="p-4 sm:p-5 bg-accent-primary/5 rounded-xl border border-accent-primary/10 relative overflow-hidden">
                                <div className="absolute top-2 right-2 text-accent-primary/15">
                                    <Sparkles className="w-12 h-12 sm:w-16 sm:h-16" strokeWidth={1.5} />
                                </div>
                                <div className="text-[10px] font-bold text-accent-primary uppercase tracking-wider mb-2">{t("landing.hero.aiInsight")}</div>
                                <p className="text-text-primary font-medium text-xs sm:text-sm leading-relaxed">
                                    {t("landing.hero.insightTextPart1")} <span className="font-bold">{t("channels.tiktok")}</span> {t("landing.hero.insightTextPart2")} <span className="font-bold">{t("channels.meta")}</span> {t("landing.hero.insightTextPart3")}
                                </p>
                                <button className="mt-4 w-full py-2 bg-white dark:bg-neutral-900 text-accent-primary text-[10px] sm:text-xs font-bold rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors">
                                    {t("landing.hero.applyRecommendation")}
                                </button>
                            </div>

                            <div className="p-4 sm:p-5 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700">
                                <h4 className="font-semibold text-xs sm:text-sm text-text-primary mb-3">{t("landing.hero.activeChannels")}</h4>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-success-500"></div> {t("channels.shopify")}
                                        </span>
                                        <span className="text-xs sm:text-sm font-medium">{t("landing.hero.connected")}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-success-500"></div> {t("channels.meta")}
                                        </span>
                                        <span className="text-xs sm:text-sm font-medium">{t("landing.hero.connected")}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Decorative Floating Elements (Figma style) */}
            <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -right-8 top-20 bg-white dark:bg-neutral-800 p-3 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 hidden lg:block"
            >
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-success-50 dark:bg-success-900/30 flex items-center justify-center text-success-600 dark:text-success-400">
                        <TrendingUp size={18} />
                    </div>
                    <div>
                        <div className="text-xs text-neutral-400">{t("landing.hero.netProfit")}</div>
                        <div className="font-bold text-text-primary">+$2,400</div>
                    </div>
                </div>
            </motion.div>

            <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -left-12 bottom-40 bg-white dark:bg-neutral-800 p-3 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 hidden lg:block"
            >
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
                        <AlertTriangle size={16} />
                    </div>
                    <div>
                        <div className="text-xs text-neutral-400">{t("landing.hero.roasAlert")}</div>
                        <div className="font-bold text-text-primary">{t("landing.hero.checkMetaAds")}</div>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};
